import { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { Acao5W2HCard } from './Acao5W2HCard';
import { STATUS_ACAO_LABELS, getStatusAcaoColor } from '@/types/plano-acao-extended';
import { cn } from '@/lib/utils';

interface Acao {
  id: string;
  what_oque: string;
  why_porque: string;
  who_quem?: string | null;
  where_onde?: string | null;
  when_quando_inicio?: string | null;
  when_quando_fim?: string | null;
  how_como?: string | null;
  how_much_quanto?: number | null;
  status: string;
  prioridade?: string | null;
  percentual_conclusao?: number | null;
  motivo_bloqueio?: string | null;
  responsavel?: { id: string; name: string } | null;
}

interface AcoesKanbanProps {
  acoes: Acao[];
  onStatusChange: (id: string, status: string) => void;
  onEdit: (acao: Acao) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  readOnly?: boolean;
  isLoading?: boolean;
}

const KANBAN_COLUMNS = [
  { id: 'pendente', label: 'Pendente', color: 'bg-slate-100' },
  { id: 'em_andamento', label: 'Em Andamento', color: 'bg-blue-100' },
  { id: 'bloqueada', label: 'Bloqueada', color: 'bg-orange-100' },
  { id: 'concluida', label: 'Concluída', color: 'bg-green-100' },
];

// Sortable Item Wrapper
const SortableAcaoCard = ({ acao, onEdit, onDelete, readOnly }: { 
  acao: Acao; 
  onEdit: () => void;
  onDelete: () => void;
  readOnly?: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: acao.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Acao5W2HCard
        acao={acao}
        onEdit={onEdit}
        onDelete={onDelete}
        readOnly={readOnly}
        isDragging={isDragging}
        dragHandleProps={listeners}
      />
    </div>
  );
};

// Droppable Column
const KanbanColumn = ({ 
  column, 
  acoes, 
  onEdit, 
  onDelete, 
  readOnly,
  onNew
}: { 
  column: typeof KANBAN_COLUMNS[0];
  acoes: Acao[];
  onEdit: (acao: Acao) => void;
  onDelete: (id: string) => void;
  readOnly?: boolean;
  onNew?: () => void;
}) => {
  const acaoIds = acoes.map(a => a.id);

  return (
    <Card className={cn("flex flex-col min-w-[280px] max-w-[320px]", column.color)}>
      <CardHeader className="pb-2 pt-3 px-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-medium">{column.label}</CardTitle>
            <Badge variant="secondary" className="h-5 px-1.5">
              {acoes.length}
            </Badge>
          </div>
          {column.id === 'pendente' && !readOnly && onNew && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onNew}>
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-2 pt-0">
        <ScrollArea className="h-[calc(100vh-22rem)]">
          <SortableContext items={acaoIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-2 pr-2">
              {acoes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Nenhuma ação
                </div>
              ) : (
                acoes.map((acao) => (
                  <SortableAcaoCard
                    key={acao.id}
                    acao={acao}
                    onEdit={() => onEdit(acao)}
                    onDelete={() => onDelete(acao.id)}
                    readOnly={readOnly}
                  />
                ))
              )}
            </div>
          </SortableContext>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export const AcoesKanban = ({ 
  acoes, 
  onStatusChange, 
  onEdit, 
  onDelete,
  onNew,
  readOnly,
  isLoading 
}: AcoesKanbanProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Group actions by status
  const acoesPorStatus = useMemo(() => {
    const grouped: Record<string, Acao[]> = {
      pendente: [],
      em_andamento: [],
      bloqueada: [],
      concluida: [],
    };

    acoes.forEach((acao) => {
      const status = acao.status || 'pendente';
      if (grouped[status]) {
        grouped[status].push(acao);
      } else {
        grouped.pendente.push(acao);
      }
    });

    return grouped;
  }, [acoes]);

  const activeAcao = activeId ? acoes.find(a => a.id === activeId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    if (readOnly) return;
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || readOnly) return;

    const activeAcao = acoes.find(a => a.id === active.id);
    if (!activeAcao) return;

    // Find which column the item was dropped into
    const overId = over.id as string;
    
    // Check if dropped over a column header
    const targetColumn = KANBAN_COLUMNS.find(col => col.id === overId);
    if (targetColumn && targetColumn.id !== activeAcao.status) {
      onStatusChange(activeAcao.id, targetColumn.id);
      return;
    }

    // Check if dropped over another card
    const overAcao = acoes.find(a => a.id === overId);
    if (overAcao && overAcao.status !== activeAcao.status) {
      onStatusChange(activeAcao.id, overAcao.status);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Carregando ações...</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {KANBAN_COLUMNS.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            acoes={acoesPorStatus[column.id] || []}
            onEdit={onEdit}
            onDelete={onDelete}
            readOnly={readOnly}
            onNew={column.id === 'pendente' ? onNew : undefined}
          />
        ))}
      </div>

      <DragOverlay>
        {activeAcao && (
          <Acao5W2HCard
            acao={activeAcao}
            isDragging
            readOnly
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default AcoesKanban;
